import React, { forwardRef, useMemo } from 'react';

import type { TypographyProps } from '@mui/material/Typography';
import Typography from '@mui/material/Typography';
import Box from '@mui/system/Box';
import type { GridRenderCellParams } from '@mui/x-data-grid-premium';

import { OverflowTooltip } from 'components/DataGridCells/OverflowTooltip';

type TypographyCellProps = TypographyProps & {
  params: GridRenderCellParams;
  wrapperSx?: object;
};

export const TypographyCell = (props: TypographyCellProps) => {
  const { children, params, ...other } = props;
  return (
    <Typography className='MuiDataGrid-cellContent' variant='inherit' sx={{ overflow: 'hidden' }} {...other}>
      {children ?? (params.value != null ? params.value : '\u00A0')}
    </Typography>
  );
};
TypographyCell.displayName = 'TypographyCell';

/**
 * A Typography cell that will show a tooltip when the text overflows. If you know that the text will never overflow,
 * use `TypographyCell` instead since it is more performant.
 */
export const TypographyWithOverflowCell = forwardRef<HTMLDivElement, TypographyCellProps>((props, ref) => {
  const { children, params, sx: otherSx, wrapperSx, ...other } = props;

  let justifyContent = 'start';
  const align = props.align ?? params?.colDef?.align;
  if (align === 'center') {
    justifyContent = 'center';
  } else if (align === 'right') {
    justifyContent = 'end';
  }

  const combinedSx = useMemo(
    () => [
      {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        minWidth: 0,
        width: '100%',
      },
      ...(Array.isArray(otherSx) ? otherSx : [otherSx]),
    ],
    [otherSx],
  );

  return (
    <Box
      className='MuiDataGrid-cellContent'
      display='flex'
      alignItems='center'
      justifyContent={justifyContent}
      sx={wrapperSx}
    >
      <OverflowTooltip title={params.value}>
        <Typography ref={ref} sx={combinedSx} variant='inherit' {...other}>
          {children ?? (params.value || '\u00A0')}
        </Typography>
      </OverflowTooltip>
    </Box>
  );
});
TypographyWithOverflowCell.displayName = 'TypographyWithOverflowCell';

export const renderTypographyCell = (params: GridRenderCellParams) => <TypographyCell params={params} />;
